import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Space, Select, message, Tooltip, Badge, Spin, Alert, Popover, Tag, Divider } from 'antd';
import {
  SaveOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  CodeOutlined,
  CloudServerOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import { OPMApi } from '../../../../api/opmApi';
import type {
  ProcessFuncCodeDetailDto,
  FunctionLanguage,
  ValidateProcessLogicResponse,
  SaveProcessCodeRequest,
} from '../../../../api/opmApi';
import ExecutionRunPanel from './ExecutionRunPanel';
import ExecutionHistoryPanel from './ExecutionHistoryPanel';
import { generateTypeDefsFromDefinition, generateFunctionSignature } from './types';

/**
 * 从旧代码中提取函数体，注入到新签名中，保留用户已写的实现逻辑
 */
function mergeBodyIntoSignature(newSig: string, oldCode: string, lang: FunctionLanguage): string {
  if (lang === 'PYTHON') {
    const oldLines = oldCode.split('\n');
    const defIdx = oldLines.findIndex(l => /^\s*(async\s+)?def\s+/.test(l));
    if (defIdx === -1) return newSig;
    const oldBody = oldLines.slice(defIdx + 1).join('\n');

    const newLines = newSig.split('\n');
    const newDefIdx = newLines.findIndex(l => /^\s*(async\s+)?def\s+/.test(l));
    if (newDefIdx === -1) return newSig;
    return [...newLines.slice(0, newDefIdx + 1), oldBody].join('\n');
  }

  // TypeScript: 找到函数声明行的 {，提取 body 内容（兼容 async/非 async）
  const funcMatch = oldCode.match(/(async\s+)?function\s+\w+[^{]*\{/);
  if (!funcMatch || funcMatch.index === undefined) return newSig;
  const bodyStart = funcMatch.index + funcMatch[0].length;
  const lastBrace = oldCode.lastIndexOf('}');
  if (lastBrace <= bodyStart) return newSig;
  const body = oldCode.slice(bodyStart, lastBrace);

  const newFuncIdx = newSig.lastIndexOf('{');
  const newLastBrace = newSig.lastIndexOf('}');
  if (newFuncIdx === -1 || newLastBrace === -1) return newSig;
  return newSig.slice(0, newFuncIdx + 1) + body + newSig.slice(newLastBrace);
}

interface FunctionExecTabProps {
  definition: ProcessFuncCodeDetailDto | null;
  ontologyId: number;
  processNodeId: string;
  inputSchema: any[] | Record<string, any> | null;
  outputSchema: any | null;
  onDefinitionUpdate: (def: ProcessFuncCodeDetailDto) => void;
}

const LANGUAGE_OPTIONS = [
  { label: 'TypeScript', value: 'TYPESCRIPT' as FunctionLanguage },
  { label: 'Python', value: 'PYTHON' as FunctionLanguage },
];

const RUNTIME_ENVS = [
  {
    id: 'default',
    label: '默认环境',
    runtimes: [
      {
        name: 'TypeScript', version: '5.0.3', icon: '🟦',
        packages: [
          { name: 'intellix-sdk', version: 'latest', description: 'Intellix 平台 SDK' },
        ],
      },
      {
        name: 'Python', version: '3.12', icon: '🐍',
        packages: [
          { name: 'intellix-sdk', version: 'latest', description: 'Intellix 平台 SDK' },
        ],
      },
    ],
  },
] as const;

function RuntimeEnvPopover({ envId }: { envId: string }) {
  const env = RUNTIME_ENVS.find(e => e.id === envId) ?? RUNTIME_ENVS[0];
  return (
    <div style={{ width: 300 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
        <CloudServerOutlined style={{ marginRight: 6 }} />{env.label}
      </div>
      {env.runtimes.map((r, i) => (
        <div key={r.name}>
          {i > 0 && <Divider style={{ margin: '8px 0' }} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{r.icon} {r.name}</span>
            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>v{r.version}</Tag>
          </div>
          <div style={{ paddingLeft: 4 }}>
            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>已安装包</div>
            {r.packages.map(p => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 12, fontFamily: 'monospace' }}>{p.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {p.description && <span style={{ fontSize: 11, color: '#aaa' }}>{p.description}</span>}
                  <Tag color="green" style={{ margin: 0, fontSize: 11 }}>{p.version}</Tag>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const MONACO_LANG_MAP: Record<FunctionLanguage, string> = {
  TYPESCRIPT: 'typescript',
  PYTHON: 'python',
};

const FunctionExecTab: React.FC<FunctionExecTabProps> = ({
  definition,
  ontologyId,
  processNodeId,
  inputSchema,
  outputSchema,
  onDefinitionUpdate,
}) => {
  // 每种语言独立存储代码
  const [allSourceCode, setAllSourceCode] = useState<Partial<Record<FunctionLanguage, string>>>({});
  const [language, setLanguage] = useState<FunctionLanguage>('TYPESCRIPT');
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateProcessLogicResponse | null>(null);
  const [typeDefs, setTypeDefs] = useState('');
  const [localTypeDefs, setLocalTypeDefs] = useState('');

  const [showRunPanel, setShowRunPanel] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [envId, setEnvId] = useState('default');

  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);

  // 当前语言显示的代码
  const sourceCode = allSourceCode[language] || '';

  useEffect(() => {
    if (definition) {
      const lang = definition.language || 'TYPESCRIPT';
      setLanguage(lang);

      const effectiveInputSchema = inputSchema ?? definition.inputSchema;
      const effectiveOutputSchema = outputSchema ?? definition.outputSchema;

      const newSig = generateFunctionSignature({
        functionName: definition.functionName,
        inputSchema: effectiveInputSchema,
        outputSchema: effectiveOutputSchema,
        language: lang,
      });

      setAllSourceCode(prev => {
        const prevCode = prev[lang] || '';
        const serverCode = definition.sourceCode?.[lang] || '';
        let nextCode: string;
        if (!prevCode.trim()) {
          // 首次加载：优先回显服务端代码
          nextCode = serverCode || newSig;
        } else {
          // 已有代码：更新签名（含最新必选/可选），保留函数体
          nextCode = mergeBodyIntoSignature(newSig, prevCode, lang);
        }
        // 将服务端其他语言代码也同步进来（不覆盖用户已编辑的）
        const merged: Partial<Record<FunctionLanguage, string>> = { ...definition.sourceCode };
        Object.keys(prev).forEach(k => {
          if (prev[k as FunctionLanguage]) merged[k as FunctionLanguage] = prev[k as FunctionLanguage];
        });
        merged[lang] = nextCode;
        return merged;
      });

      // 根据最新 schema 生成本地类型声明
      const localDefs = generateTypeDefsFromDefinition({
        functionName: definition.functionName,
        inputSchema: effectiveInputSchema,
        outputSchema: effectiveOutputSchema,
        processName: definition.processName,
      });
      setLocalTypeDefs(localDefs);
    }
  }, [definition, inputSchema, outputSchema]);

  useEffect(() => {
    if (processNodeId && ontologyId) {
      loadTypeDefs();
    }
  }, [processNodeId, ontologyId]);

  const loadTypeDefs = async () => {
    try {
      const defs = await OPMApi.getProcessTypeDefs(ontologyId, processNodeId);
      setTypeDefs(defs || '');
    } catch (error) {
      console.error('获取类型定义失败:', error);
      setTypeDefs('');
    }
  };

  const setupMonacoTypeDefs = useCallback(
    (monaco: typeof Monaco, typeDefsStr: string) => {
      if (!typeDefsStr || !monaco) return;

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tsLang = (monaco.languages as any).typescript;
        const tsDefaults = tsLang.typescriptDefaults;

        tsDefaults.setCompilerOptions({
          target: tsLang.ScriptTarget.ES2020,
          allowNonTsExtensions: true,
          moduleResolution: tsLang.ModuleResolutionKind.NodeJs,
          module: tsLang.ModuleKind.CommonJS,
          noEmit: true,
          esModuleInterop: true,
          strict: false,
          skipLibCheck: true,
          noImplicitAny: false,
        });

        tsDefaults.setDiagnosticsOptions({
          noSemanticValidation: false,
          noSyntaxValidation: false,
          diagnosticCodesToIgnore: [1108],
        });

        tsDefaults.setExtraLibs([]);
        if (typeDefsStr) {
          tsDefaults.addExtraLib(typeDefsStr, 'file:///process-types.d.ts');
        }
      } catch (error) {
        console.error('加载类型定义失败:', error);
      }
    },
    []
  );

  const handleEditorDidMount = (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco
  ) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    editor.focus();
    editor.updateOptions({ readOnly: false });

    // 若 sourceCode 已含 interface 声明，跳过 localTypeDefs 避免重复声明冲突(TS2687)
    const defs = sourceCode ? [typeDefs] : [localTypeDefs, typeDefs];
    const combinedDefs = defs.filter(Boolean).join('\n\n');
    if (combinedDefs) {
      setTimeout(() => setupMonacoTypeDefs(monaco, combinedDefs), 100);
    }
  };

  useEffect(() => {
    const defs = sourceCode ? [typeDefs] : [localTypeDefs, typeDefs];
    const combinedDefs = defs.filter(Boolean).join('\n\n');
    if (combinedDefs && monacoRef.current) {
      setupMonacoTypeDefs(monacoRef.current, combinedDefs);
    }
  }, [typeDefs, localTypeDefs, sourceCode, setupMonacoTypeDefs]);

  const handleSave = async () => {
    if (!processNodeId) return;
    setSaving(true);
    try {
      const req: SaveProcessCodeRequest = {
        sourceCode: allSourceCode,
        language,
      };
      const result = await OPMApi.saveProcessCode(ontologyId, processNodeId, req);
      onDefinitionUpdate(result);
      message.success('保存成功');
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!processNodeId) return;
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await OPMApi.validateProcessLogic(ontologyId, processNodeId, {
        processNodeId,
        sourceCode: allSourceCode,
        language,
        functionName: definition?.functionName,
      });
      setValidationResult(result);
      if (result.valid) {
        message.success('代码验证通过');
      } else {
        message.warning(result.message || '代码验证失败');
      }
    } catch (error: any) {
      message.error(error.message || '验证失败');
    } finally {
      setValidating(false);
    }
  };

  const handleLanguageChange = (lang: FunctionLanguage) => {
    setLanguage(lang);
    setValidationResult(null);
    // 若切换到的语言尚无代码，生成默认签名
    if (!allSourceCode[lang]) {
      const newSig = generateFunctionSignature({
        functionName: definition?.functionName,
        inputSchema: inputSchema ?? definition?.inputSchema,
        outputSchema: outputSchema ?? definition?.outputSchema,
        language: lang,
      });
      setAllSourceCode(prev => ({ ...prev, [lang]: newSig }));
    }
  };

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* 语言选择 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Space>
          <CodeOutlined />
          <Select
            value={language}
            onChange={handleLanguageChange}
            options={LANGUAGE_OPTIONS}
            size="small"
            style={{ width: 130 }}
          />
          {definition?.status && (
            <Badge
              status={
                definition.status === 'VALID'
                  ? 'success'
                  : definition.status === 'INVALID'
                  ? 'error'
                  : 'default'
              }
              text={definition.status}
              style={{ fontSize: 12 }}
            />
          )}
        </Space>
        <Space size={4}>
          <CloudServerOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
          <Select
            value={envId}
            onChange={setEnvId}
            size="small"
            style={{ width: 120 }}
            options={RUNTIME_ENVS.map(e => ({ label: e.label, value: e.id }))}
          />
          <Popover
            content={<RuntimeEnvPopover envId={envId} />}
            title={null}
            trigger="click"
            placement="bottomRight"
          >
            <Button size="small" type="text" icon={<InfoCircleOutlined />} style={{ color: '#8c8c8c' }} />
          </Popover>
        </Space>
      </div>

      {/* 验证结果 */}
      {validationResult && (
        <Alert
          message={validationResult.valid ? '验证通过' : '验证失败'}
          description={validationResult.message}
          type={validationResult.valid ? 'success' : 'error'}
          showIcon
          closable
          onClose={() => setValidationResult(null)}
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Monaco Editor 容器 */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <Editor
            height={showRunPanel ? '320px' : '480px'}
            language={MONACO_LANG_MAP[language]}
            path={`process-function.${language === 'TYPESCRIPT' ? 'ts' : 'py'}`}
            value={sourceCode}
            onChange={(value) => setAllSourceCode(prev => ({ ...prev, [language]: value || '' }))}
            onMount={handleEditorDidMount}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              formatOnPaste: true,
              suggestOnTriggerCharacters: true,
              quickSuggestions: true,
              parameterHints: { enabled: true },
              autoIndent: 'full',
              folding: true,
              wordWrap: 'on',
              readOnly: false,
              renderLineHighlight: 'line',
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                verticalScrollbarSize: 12,
                horizontalScrollbarSize: 10,
              },
            }}
            theme="vs-light"
          />
        </div>

        {/* 悬浮操作栏 */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 20,
            zIndex: 10,
            display: 'flex',
            gap: 6,
            background: 'rgba(255,255,255,0.92)',
            borderRadius: 6,
            padding: '4px 8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Tooltip title="编译校验">
            <Button
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              loading={validating}
            />
          </Tooltip>
          <Tooltip title="保存 (Ctrl+S)">
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            />
          </Tooltip>
          <Tooltip title="试运行">
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => setShowRunPanel(v => !v)}
              style={showRunPanel ? { color: '#fff', background: '#52c41a', borderColor: '#52c41a' } : { color: '#52c41a', borderColor: '#b7eb8f' }}
            />
          </Tooltip>
          <Tooltip title="执行记录">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setShowHistory(true)}
            />
          </Tooltip>
        </div>
      </div>

      {/* 试运行内联面板 */}
      {showRunPanel && (
        <ExecutionRunPanel
          ontologyId={ontologyId}
          processNodeId={processNodeId}
          inputSchema={definition?.inputSchema ?? null}
          outputSchema={definition?.outputSchema ?? null}
          onClose={() => setShowRunPanel(false)}
          onExecutionComplete={() => {}}
        />
      )}

      {/* 执行记录 Drawer */}
      <ExecutionHistoryPanel
        visible={showHistory}
        onClose={() => setShowHistory(false)}
        ontologyId={ontologyId}
        processNodeId={processNodeId}
      />
    </div>
  );
};

export default FunctionExecTab;
